/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Chat session status
 */
export const ChatSummaryStatus = {
  Success: "success",
  Error: "error",
} as const;
/**
 * Chat session status
 */
export type ChatSummaryStatus = ClosedEnum<typeof ChatSummaryStatus>;

/**
 * Summary information for a chat session
 */
export type ChatSummary = {
  /**
   * Chat session duration in seconds
   */
  durationSeconds: number;
  /**
   * Latest log timestamp in Unix nanoseconds (string for JS int64 precision)
   */
  endTimeUnixNano: string;
  /**
   * Chat session ID
   */
  gramChatId: string;
  /**
   * Total number of logs in this chat session
   */
  logCount: number;
  /**
   * Number of LLM completion messages in this chat session
   */
  messageCount: number;
  /**
   * LLM model used in this chat session
   */
  model?: string | undefined;
  /**
   * Earliest log timestamp in Unix nanoseconds (string for JS int64 precision)
   */
  startTimeUnixNano: string;
  /**
   * Chat session status
   */
  status: ChatSummaryStatus;
  /**
   * Number of tool calls in this chat session
   */
  toolCallCount: number;
  /**
   * Total input tokens used
   */
  totalInputTokens: number;
  /**
   * Total output tokens used
   */
  totalOutputTokens: number;
  /**
   * Total tokens used (input + output)
   */
  totalTokens: number;
  /**
   * User ID associated with this chat session
   */
  userId?: string | undefined;
};

/** @internal */
export const ChatSummaryStatus$inboundSchema: z.ZodMiniEnum<
  typeof ChatSummaryStatus
> = z.enum(ChatSummaryStatus);

/** @internal */
export const ChatSummary$inboundSchema: z.ZodMiniType<ChatSummary, unknown> = z
  .pipe(
    z.object({
      duration_seconds: z.number(),
      end_time_unix_nano: z.string(),
      gram_chat_id: z.string(),
      log_count: z.int(),
      message_count: z.int(),
      model: z.optional(z.string()),
      start_time_unix_nano: z.string(),
      status: ChatSummaryStatus$inboundSchema,
      tool_call_count: z.int(),
      total_input_tokens: z.int(),
      total_output_tokens: z.int(),
      total_tokens: z.int(),
      user_id: z.optional(z.string()),
    }),
    z.transform((v) => {
      return remap$(v, {
        "duration_seconds": "durationSeconds",
        "end_time_unix_nano": "endTimeUnixNano",
        "gram_chat_id": "gramChatId",
        "log_count": "logCount",
        "message_count": "messageCount",
        "start_time_unix_nano": "startTimeUnixNano",
        "tool_call_count": "toolCallCount",
        "total_input_tokens": "totalInputTokens",
        "total_output_tokens": "totalOutputTokens",
        "total_tokens": "totalTokens",
        "user_id": "userId",
      });
    }),
  );

export function chatSummaryFromJSON(
  jsonString: string,
): SafeParseResult<ChatSummary, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ChatSummary$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ChatSummary' from JSON`,
  );
}
